import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';

interface IAccordionSubItem {
  id: string;
  name?: string;
  code?: string;
  estimatedResponseTime?: number;
}

export interface IAccordionItem {
  id: string;
  name?: string;
  shortDescription?: string | ReactElement | Record<string, string>;
  categoryCode?: string;
  items?: IAccordionSubItem[];
  importance?: string;
  active?: boolean;
  assigned?: boolean;
  selected?: boolean;
}

type TAccordionStateItem<T extends IAccordionItem> = T & {
  active?: boolean;
  assigned?: boolean;
};

export const useAccordion = <T extends IAccordionItem>(items: T[]) => {
  const [accItems, setItems] = useState<TAccordionStateItem<T>[]>([]);

  const assignedItemsArr = useMemo(
    () => accItems.filter(({ assigned }) => assigned === true),
    [accItems]
  );

  const notAssignedItemsArr = useMemo(
    () => accItems.filter(({ assigned }) => assigned === false),
    [accItems]
  );

  const mapedAccordionItems = useMemo(
    () => items.map(item => ({ ...item, active: false, assigned: item?.selected ?? true })),
    [items]
  );

  const setItemStatus = useCallback(
    (id: string) => {
      const newAccState = accItems.map(item =>
        item.id === id ? { ...item, active: !item.active } : item
      );
      setItems(newAccState);
    },
    [accItems]
  );

  const closeOpenAll = (active: boolean) => {
    const newAccState = accItems.map(item => ({ ...item, active }));
    setItems(newAccState);
  };

  const extendButtonState = useMemo(() => {
    return !!accItems.find(item => item.active === true);
  }, [accItems]);

  const changeItemAssigment = useCallback(
    (id: string) => {
      const newAccState = accItems.map(item =>
        item.id === id ? { ...item, assigned: !item.assigned } : item
      );
      setItems(newAccState);
    },
    [accItems]
  );

  const changeItemsAssigment = useCallback(
    (ids: string[]) => {
      const newAccState = accItems.map(item =>
        ids.includes(item.id) ? { ...item, assigned: !item.assigned } : item
      );
      setItems(newAccState);
    },
    [accItems]
  );

  const changeMultipleItemsAssignment = useCallback(
    (ids: string[]) => {
      if (ids.length === 0) {
        setItems(accItems => accItems.map(item => ({ ...item, assigned: false })));
        return;
      }

      const newAccState = accItems.map(item =>
        ids.includes(item.id) ? { ...item, assigned: true } : { ...item, assigned: false }
      );

      setItems(newAccState);
    },
    [accItems]
  );

  useEffect(() => {
    setItems(mapedAccordionItems);
  }, [mapedAccordionItems]);

  return {
    itemsArr: accItems,
    assignedItemsArr,
    notAssignedItemsArr,
    setItemStatus,
    closeOpenAll,
    extendButtonState,
    changeItemAssigment,
    changeItemsAssigment,
    changeMultipleItemsAssignment,
  };
};
